import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MoreHorizontal, Copy, Clipboard, Check, Eye, Download, Search, Upload, FileSpreadsheet, AlertCircle } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Mask } from "@/utils/mask";
import { getShowContests } from "@/Api/contests";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SharingBtn } from "@/components/Sharing";
import { CreateLoaderInput, LoaderSpin } from "@/components/Loaders";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToastNavigate } from "@/utils/toastNavigate";
import {
  storeDistributionsUnityToUnityWithFiles,
  getUnityToUnityBalancesSimplified,
} from "@/Api/distributionsUnityToUnity";
import {
  distributionBetweenUnitiesBulkWithFiles,
  getUnityToUnityDistributionDetails,
  exportBetweenUnitiesDistributionTemplate,
  importBetweenUnitiesDistributionPreview,
  confirmImportBetweenUnitiesDistribution,
} from "@/Api/distributions";
import { FileUpload } from "../../components/FileUpload";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";

// Nova estrutura simplificada do backend para Unity-to-Unity
interface SimplifiedUnityBalance {
  current_balance: string;
  held_balance: string;
  total_distributed: string;
  consumed_quantity: string;
  quota_total_quantity: string;
}

interface SimplifiedUnity {
  id: number;
  name: string;
  contest_product_balance: SimplifiedUnityBalance;
}

interface SimplifiedProduct {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: number;
    quota_total_distributed: number;
  };
  unities: SimplifiedUnity[];
}

interface Product {
  id: number;
  product_name: string;
  product_classification: {
    quota_quantity: number;
    sale_price: number;
  };
  lottery_id: number;
}

const schema = z.object({
  contest_id: z.number(),
  lottery_id: z.number(),
  contest_product_classification_id: z.number(),
  from_unity_id: z.number(),
  unities: z.array(
    z.object({
      id: z.number(),
      quota_quantity: z.number().min(0, "Quantidade mínima é 0"),
    })
  ),
});

type SchemaType = z.infer<typeof schema>;

export const TableData = ({
  dataApi,
  pageActions,
  unityId,
}: {
  dataApi: any;
  pageActions: any;
  unityId: number;
}) => {
  const [searchParams] = useSearchParams();
  const contestId = searchParams.get("q");
  const showBtnRefs = useRef<{ [key: string]: HTMLElement }>({});
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (contestId) {
      const decodedId = atob(contestId);
      const contest = dataApi?.data.find(
        (item) => item.id === Number(decodedId)
      );
      if (contest && showBtnRefs.current[contest.uuid]) {
        const button =
          showBtnRefs.current[contest.uuid].querySelector("button");
        if (button) {
          button.click();
        }
      }
    }
  }, [contestId, dataApi?.data]);

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Data de validade</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {dataApi?.data.map((item) => (
            <TableRow key={item?.id}>
              <TableCell className="truncate max-w-80">{item?.name}</TableCell>
              <TableCell>
                <span
                  className="px-2 py-1 rounded text-white text-sm font-medium"
                  style={{ backgroundColor: item?.lottery?.color }}
                >
                  {item?.lottery?.name}
                </span>
              </TableCell>
              <TableCell>
                {item?.validation_date
                  ? Mask.transformMaskBirthdayInBr(item?.validation_date)
                  : "Não informado"}
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <div
                    ref={(el) => el && (showBtnRefs.current[item.uuid] = el)}
                  >
                    <If condition={hasPermission("create-distributions")}>
                      <SharingBtn
                        labels={{ title: "Distribuir entre Unidades" }}
                        params={{
                          func: getShowContests,
                          funcParams: item?.uuid,
                        }}
                        className="sm:max-w-[700px]"
                        render={(responseApi: any) => (
                          <InsideContestModal
                            contestData={responseApi}
                            unityId={unityId}
                          />
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}

          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

function InsideContestModal({
  contestData,
  unityId,
}: {
  contestData: any;
  unityId: number;
}) {
  const { toast } = useToastNavigate();
  const [simplifiedData, setSimplifiedData] = useState<SimplifiedProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] =
    useState<any>(null);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [distributingProductId, setDistributingProductId] = useState<
    number | null
  >(null);
  const [quotaStates, setQuotaStates] = useState<{
    [key: string]: { unityId: number; quota: number }[];
  }>({});
  // Ref para armazenar as alterações do usuário (não é resetado pelo fetchPage)
  const userChangesRef = useRef<{
    [productId: string]: { unityId: number; quota: number }[];
  }>({});
  const [confirmDialogProduct, setConfirmDialogProduct] = useState<any>(null);
  const [showDistributeAllDialog, setShowDistributeAllDialog] = useState(false);
  // Snapshot dos dados a distribuir quando o dialog é aberto
  const [distributeAllSnapshot, setDistributeAllSnapshot] = useState<{
    [productId: string]: { unityId: number; quota: number }[];
  } | null>(null);
  const [isDistributingAll, setIsDistributingAll] = useState(false);
  const [distributeAllFiles, setDistributeAllFiles] = useState<File[]>([]);
  const [copiedDistribution, setCopiedDistribution] = useState<
    | {
        unityId: number;
        quota: number;
      }[]
    | null
  >(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<{
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    from: number;
    to: number;
  } | null>(null);
  // Filtro por produto
  const [productFilter, setProductFilter] = useState("");
  // Cache de nomes de produtos e unidades para exibir no dialog
  const [productNamesCache, setProductNamesCache] = useState<{
    [productId: string]: {
      productName: string;
      unities: { [unityId: string]: string };
    };
  }>({});

  // Import Excel states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isConfirmingImport, setIsConfirmingImport] = useState(false);
  const [importFiles, setImportFiles] = useState<File[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const fetchPage = async (page: number, filter?: string) => {
    setIsLoading(true);
    try {
      const searchFilter = filter !== undefined ? filter : productFilter;
      const productName = searchFilter ? `B${searchFilter}` : undefined;
      const response = await getUnityToUnityBalancesSimplified(
        String(contestData.id),
        page,
        100,
        productName
      );

      if (response.data?.status) {
        const responseData = response.data.response;
        const data = responseData.data as SimplifiedProduct[];
        setSimplifiedData(data);
        setPaginationInfo({
          current_page: responseData.current_page,
          per_page: responseData.per_page,
          total: responseData.total,
          last_page: responseData.last_page,
          from: responseData.from,
          to: responseData.to,
        });

        // Acumular quotaStates - manter alterações de outras páginas
        setQuotaStates((prev) => {
          const newStates = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            // Só inicializa se não existir ainda (mantém alterações feitas pelo usuário)
            if (!newStates[productId]) {
              newStates[productId] = product.unities
                .filter((unity) => unity.id !== unityId)
                .map((unity) => ({
                  unityId: unity.id,
                  quota: 0,
                }));
            }
          });
          return newStates;
        });

        // Cache de nomes de produtos e unidades para exibir no dialog
        setProductNamesCache((prev) => {
          const newCache = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            const unitiesMap: { [unityId: string]: string } = {};
            product.unities.forEach((unity) => {
              unitiesMap[unity.id] = unity.name;
            });
            newCache[productId] = {
              productName: product.contest_product_classification.product_name,
              unities: unitiesMap,
            };
          });
          return newCache;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados simplificados:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar as distribuições.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(currentPage);
  }, [contestData.id, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (paginationInfo?.last_page || 1)) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    setProductFilter(numericValue);
  };

  const handleFilterSearch = () => {
    setCurrentPage(1);
    fetchPage(1, productFilter);
  };

  const handleFilterClear = () => {
    setProductFilter("");
    setCurrentPage(1);
    fetchPage(1, "");
  };

  // Export Excel template
  const handleExportTemplate = async () => {
    setIsExporting(true);
    try {
      const blob = await exportBetweenUnitiesDistributionTemplate(String(contestData.id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileName = `distribuicao_entre_unidades_${contestData.lottery?.name}_${contestData.name}_${new Date().toISOString().split("T")[0]}.xlsx`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        description: "Modelo Excel exportado com sucesso!",
        action: <Download className="w-4 h-4 text-green-600" />,
      });
    } catch (error) {
      console.error("Erro ao exportar template:", error);
      toast({
        variant: "destructive",
        title: "Erro ao exportar",
        description: "Não foi possível exportar o modelo Excel.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Import Excel file preview
  const handleImportFile = async (file: File) => {
    setImportFile(file);
    setIsImporting(true);
    setImportPreview(null);
    try {
      const response = await importBetweenUnitiesDistributionPreview(String(contestData.id), file);
      if (response.data?.status) {
        setImportPreview(response.data.response);
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao processar arquivo",
          description: response.data?.message || "O arquivo não pôde ser processado.",
        });
      }
    } catch (error: any) {
      console.error("Erro ao importar arquivo:", error);
      toast({
        variant: "destructive",
        title: "Erro ao importar",
        description: error?.response?.data?.message || "Não foi possível processar o arquivo Excel.",
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Confirm import
  const handleConfirmImport = async () => {
    if (!importPreview || importFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo de comprovação.",
      });
      return;
    }

    setIsConfirmingImport(true);
    try {
      const response = await confirmImportBetweenUnitiesDistribution(
        String(contestData.id),
        importPreview.distributions,
        importFiles
      );
      if (response.data?.status) {
        toast({
          description: "Distribuição importada com sucesso!",
          action: <Check className="w-4 h-4 text-green-600" />,
        });
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview(null);
        setImportFiles([]);
        // Refresh data
        await fetchPage(currentPage);
      } else {
        toast({
          variant: "destructive",
          title: "Erro ao confirmar importação",
          description: response.data?.message || "Não foi possível confirmar a importação.",
        });
      }
    } catch (error: any) {
      console.error("Erro ao confirmar importação:", error);
      toast({
        variant: "destructive",
        title: "Erro ao confirmar",
        description: error?.response?.data?.message || "Não foi possível confirmar a importação.",
      });
    } finally {
      setIsConfirmingImport(false);
    }
  };

  function getDistributedQuota(productId: number, unityId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    const unity = product?.unities.find((u) => u.id === unityId);
    return parseInt(unity?.contest_product_balance.total_distributed || "0");
  }

  function getQuotaValidationInfo(
    productId: number,
    currentInputs: { unityId: number; quota: number }[]
  ) {
    const totalInputQuotas = currentInputs.reduce(
      (sum, item) => sum + item.quota,
      0
    );
    const availableBalance = getAvailableBalance(productId);
    const withHeldBalance = getWithHeldBalance(productId);

    const isOverLimit = totalInputQuotas > availableBalance;
    const remaining = availableBalance - totalInputQuotas;

    return {
      totalInputQuotas,
      availableBalance,
      withHeldBalance,
      isOverLimit,
      remaining,
      canProceed: !isOverLimit,
    };
  }

  function getAvailableBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return 0;

    // Saldo da unidade de ORIGEM (from_unity), não do concurso inteiro:
    // quota_total_quantity - quota_total_distributed é agregado de todas as unidades
    // e zera mesmo quando esta unidade ainda tem cotas próprias não repassadas.
    const currentUnity = product.unities.find((u) => u.id === unityId);
    return parseInt(currentUnity?.contest_product_balance.current_balance || "0");
  }

  function getWithHeldBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return 0;

    // Held balance da unidade atual
    const currentUnity = product.unities.find((u) => u.id === unityId);
    return parseInt(currentUnity?.contest_product_balance.held_balance || "0");
  }

  function handleQuotaChange(
    productId: number,
    targetUnityId: number,
    quota: number
  ) {
    setQuotaStates((prev) => ({
      ...prev,
      [productId]: prev[productId]?.map((item) =>
        item.unityId === targetUnityId ? { ...item, quota } : item
      ) || [{ unityId: targetUnityId, quota }],
    }));

    // Também salva no ref para garantir persistência
    if (!userChangesRef.current[productId]) {
      userChangesRef.current[productId] = [];
    }
    const existingIndex = userChangesRef.current[productId].findIndex(
      (item) => item.unityId === targetUnityId
    );
    if (existingIndex >= 0) {
      userChangesRef.current[productId][existingIndex].quota = quota;
    } else {
      userChangesRef.current[productId].push({ unityId: targetUnityId, quota });
    }
  }

  function handleCopyDistribution(productId: number) {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return;

    const distributionToCopy = product.unities
      .filter((unity) => unity.id !== unityId) // Não incluir a unidade atual
      .map((unity) => ({
        unityId: unity.id,
        quota: parseInt(unity.contest_product_balance.total_distributed || "0"),
      }));
    setCopiedDistribution(distributionToCopy);
    toast({
      description: "Distribuição copiada com sucesso!",
      action: <Copy className="w-4 h-4" />,
    });
  }

  function handleCopyCurrentDistribution(productId: number) {
    const currentQuotas = quotaStates[productId] || [];
    const nonZeroQuotas = currentQuotas.filter((quota) => quota.quota > 0);

    if (nonZeroQuotas.length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhuma cota foi definida para copiar.",
      });
      return;
    }

    setCopiedDistribution(
      nonZeroQuotas.map((quota) => ({
        unityId: quota.unityId,
        quota: quota.quota,
      }))
    );

    const totalQuotas = nonZeroQuotas.reduce(
      (sum, quota) => sum + quota.quota,
      0
    );
    toast({
      description: `Distribuição atual copiada: ${totalQuotas} cotas em ${nonZeroQuotas.length} unidade(s)`,
      action: <Copy className="w-4 h-4" />,
    });
  }

  function handlePasteDistribution(productId: number) {
    if (!copiedDistribution || copiedDistribution.length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhuma distribuição copiada encontrada.",
      });
      return;
    }

    setQuotaStates((prev) => ({
      ...prev,
      [productId]: prev[productId].map((item) => {
        const match = copiedDistribution.find(
          (q) => q.unityId === item.unityId
        );
        return match ? { ...item, quota: match.quota } : item;
      }),
    }));

    // Também salva no ref para garantir persistência
    if (!userChangesRef.current[productId]) {
      userChangesRef.current[productId] = [];
    }
    copiedDistribution.forEach((copied) => {
      const existingIndex = userChangesRef.current[productId].findIndex(
        (item) => item.unityId === copied.unityId
      );
      if (existingIndex >= 0) {
        userChangesRef.current[productId][existingIndex].quota = copied.quota;
      } else {
        userChangesRef.current[productId].push({ unityId: copied.unityId, quota: copied.quota });
      }
    });

    toast({
      description: "Distribuição colada com sucesso!",
      action: <Clipboard className="w-4 h-4" />,
    });
  }

  async function handleShowDetails(productId: number, productName: string) {
    setSelectedProductDetails({ id: productId, name: productName });
    const { data, ok } = await getUnityToUnityDistributionDetails(
      String(contestData.id),
      productId
    );

    if (ok) {
      setProductDetails(data.response?.distributions || []);
      setShowDetailsModal(true);
    }
  }

  async function handleSubmitDistribution(product: Product) {
    if (selectedFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "É obrigatório anexar pelo menos um arquivo para realizar a distribuição.",
      });
      return;
    }

    setDistributingProductId(product.id);
    const localQuotas = quotaStates[product.id] || [];

    // Para Unity-to-Unity, consideramos qualquer valor > 0 como uma distribuição válida
    const modifiedUnities = localQuotas.filter((currentQuota) => {
      return currentQuota.quota > 0;
    });

    if (modifiedUnities.length === 0) {
      toast({
        description: "Nenhuma alteração foi detectada para enviar.",
      });
      setDistributingProductId(null);
      return;
    }

    const dataToSubmit: SchemaType = {
      contest_id: contestData.id,
      lottery_id: product.lottery_id,
      contest_product_classification_id: product.id,
      from_unity_id: unityId,
      unities: modifiedUnities.map((sq) => ({
        id: sq.unityId,
        quota_quantity: sq.quota,
      })),
    };

    const parseResult = schema.safeParse(dataToSubmit);
    if (!parseResult.success) {
      toast({
        variant: "destructive",
        description: "Verifique os campos de distribuição.",
      });
      setDistributingProductId(null);
      return;
    }

    const validationInfo = getQuotaValidationInfo(product.id, localQuotas);

    if (validationInfo.isOverLimit) {
      const excess =
        validationInfo.totalInputQuotas - validationInfo.availableBalance;
      toast({
        variant: "destructive",
        title: "Distribuição bloqueada",
        description: `Não é possível prosseguir. Você está tentando distribuir ${
          validationInfo.totalInputQuotas
        } cotas, mas há apenas ${
          validationInfo.availableBalance
        } disponíveis. Excesso: ${excess} cotas.${
          validationInfo.withHeldBalance > 0
            ? ` (${validationInfo.withHeldBalance} em processamento)`
            : ""
        }`,
      });
      setDistributingProductId(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("contest_id", String(contestData.id));
      formData.append("lottery_id", String(product.lottery_id));
      formData.append("contest_product_classification_id", String(product.id));
      formData.append("from_unity_id", String(unityId));

      modifiedUnities.forEach((unity, index) => {
        formData.append(`unities[${index}][id]`, String(unity.unityId));
        formData.append(`unities[${index}][quota_quantity]`, String(unity.quota));
      });

      selectedFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const { ok } = await storeDistributionsUnityToUnityWithFiles(formData);
      if (!ok) {
        throw new Error("Erro ao registrar distribuição");
      }
      toast({
        description: `Distribuição salva com sucesso (${
          modifiedUnities.length
        } unidade${modifiedUnities.length > 1 ? "s" : ""} atualizada${
          modifiedUnities.length > 1 ? "s" : ""
        })`,
        action: <Check color="#1edc2b" />,
      });

      setSelectedFiles([]);

      // Recarregar dados simplificados após salvar
      const response = await getUnityToUnityBalancesSimplified(
        String(contestData.id)
      );
      if (response.data?.status) {
        const data = response.data.response as SimplifiedProduct[];
        setSimplifiedData(data);

        // Atualizar states após salvar - reset inputs to 0
        const newQuotaStates: {
          [key: string]: { unityId: number; quota: number }[];
        } = {};

        data.forEach((product) => {
          const productId = product.contest_product_classification.id;
          const resetUnityQuotas = product.unities.map((unity) => ({
            unityId: unity.id,
            quota: 0, // Reset inputs to 0 after save
          }));

          newQuotaStates[productId] = [...resetUnityQuotas];
        });

        setQuotaStates(newQuotaStates);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        description:
          error instanceof Error ? error.message : "Erro ao criar distribuição",
      });
    } finally {
      setDistributingProductId(null);
    }
  }

  const handleOpenDistributeAllDialog = () => {
    const snapshot: { [productId: string]: { unityId: number; quota: number }[] } = {};

    // 1. Primeiro, pega do userChangesRef (alterações de páginas anteriores)
    Object.entries(userChangesRef.current).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    // 2. Depois, pega do quotaStates atual (página atual) - sobrescreve se já existir
    Object.entries(quotaStates).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    setDistributeAllSnapshot(snapshot);
    setShowDistributeAllDialog(true);
  };

  async function handleDistributeAll() {
    if (distributeAllFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "É obrigatório anexar um arquivo para distribuir.",
      });
      return;
    }

    if (!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhum produto para distribuir.",
      });
      return;
    }

    setLoading(true);
    setIsDistributingAll(true);
    setShowDistributeAllDialog(false);

    const distributions: Array<{
      contest_product_classification_id: number;
      from_unity_id: number;
      unities: Array<{ id: number; quota_quantity: number }>;
    }> = [];
    let skippedCount = 0;

    // Usar o snapshot capturado quando o dialog foi aberto
    const allProductIds = Object.keys(distributeAllSnapshot);

    for (const productIdStr of allProductIds) {
      const productId = parseInt(productIdStr);
      const unitiesToDistribute = distributeAllSnapshot[productId] || [];

      if (unitiesToDistribute.length === 0) {
        skippedCount++;
        continue;
      }

      // Só valida limite se o produto está na página atual (simplifiedData)
      const productInCurrentPage = simplifiedData.find(
        (p) => p.contest_product_classification.id === productId
      );
      if (productInCurrentPage) {
        const validationInfo = getQuotaValidationInfo(productId, unitiesToDistribute);
        if (validationInfo.isOverLimit) {
          skippedCount++;
          continue;
        }
      }

      distributions.push({
        contest_product_classification_id: productId,
        from_unity_id: unityId,
        unities: unitiesToDistribute.map((sq) => ({
          id: sq.unityId,
          quota_quantity: sq.quota,
        })),
      });
    }

    if (distributions.length === 0) {
      setLoading(false);
      setIsDistributingAll(false);
      toast({
        variant: "default",
        description: "Nenhuma distribuição válida para salvar.",
      });
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    try {
      const formData = new FormData();
      formData.append("contest_id", String(contestData.id));
      formData.append("lottery_id", String(contestData?.product_classifications?.[0]?.lottery_id || 0));
      formData.append("distributions", JSON.stringify(distributions));
      distributeAllFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const { ok } = await distributionBetweenUnitiesBulkWithFiles(formData);

      if (ok) {
        successCount = distributions.length;
      } else {
        errorCount = distributions.length;
      }
    } catch (error) {
      errorCount = distributions.length;
    }

    try {
      setLoading(false);
      // Limpar todos os states após distribuir
      setQuotaStates({});
      setProductNamesCache({});
      setDistributeAllSnapshot(null);
      userChangesRef.current = {};
      // Recarregar dados simplificados após salvar
      await fetchPage(currentPage);
    } catch (error) {
      console.error("Erro ao atualizar distribuições:", error);
    }

    setIsDistributingAll(false);
    setDistributeAllFiles([]);
    setDistributeAllSnapshot(null);

    if (successCount > 0 && errorCount === 0) {
      const message =
        skippedCount > 0
          ? `${successCount} distribuições salvas com sucesso! ${skippedCount} produtos foram ignorados.`
          : `Todas as ${successCount} distribuições foram salvas com sucesso!`;
      toast({
        description: message,
        action: <Check color="#1edc2b" />,
      });
    } else if (successCount > 0 && errorCount > 0) {
      toast({
        description: `${successCount} distribuições salvas com sucesso, ${errorCount} falharam.`,
        action: <Check color="#ff8000" />,
      });
    } else {
      toast({
        variant: "destructive",
        description: "Erro ao salvar todas as distribuições",
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoaderSpin />
      </div>
    );
  }

  // Encontrar nome da unidade atual
  const currentUnityName =
    simplifiedData[0]?.unities.find((u) => u.id === unityId)?.name || "Unidade";

  return (
    <>
      <div className="h-[70vh] overflow-hidden flex flex-col" style={{ maxHeight: "70vh" }}>
        <div className="w-full h-auto flex-shrink-0">
          {/* Header melhorado do concurso */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 mb-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div
                  className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
                  style={{ backgroundColor: contestData?.lottery?.color || '#023859' }}
                >
                  {contestData?.lottery?.name}
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    {contestData?.name}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Validade: {contestData?.validation_date
                      ? Mask.transformMaskBirthdayInBr(contestData?.validation_date)
                      : "Não informado"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Distribuindo de</p>
                  <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">{currentUnityName}</p>
                </div>
                <Button
                  onClick={handleOpenDistributeAllDialog}
                  className={cn("flex gap-2", colors.bg_primary)}
                  disabled={isDistributingAll || isLoading}
                >
                  {isDistributingAll ? (
                    <>
                      Distribuindo... <LoaderSpin />
                    </>
                  ) : (
                    "Distribuir Tudo"
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Botões de Exportar/Importar Excel */}
          <div className="flex items-center justify-end gap-2 mb-4">
            <span className="text-sm text-slate-500 dark:text-slate-400 mr-2">
              Modelos de Excel:
            </span>
            <Button
              onClick={handleExportTemplate}
              disabled={isExporting}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              {isExporting ? (
                <LoaderSpin />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Exportar Modelo
            </Button>
            <Button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              <Upload className="w-4 h-4" />
              Importar Excel
            </Button>
          </div>

          {/* Filtro por produto */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">B:</span>
              <Input
                type="text"
                placeholder="Ex: 200"
                value={productFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleFilterSearch()}
                className="w-24"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleFilterSearch}
              className="flex items-center gap-1"
            >
              <Search className="w-4 h-4" />
              Buscar
            </Button>
            {productFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFilterClear}
              >
                Limpar
              </Button>
            )}
            {paginationInfo && (
              <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                Exibindo {paginationInfo.from}-{paginationInfo.to} de {paginationInfo.total} produtos
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 flex-1 overflow-y-auto">
          <div>
            <Table>
              <TableHeader className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Qtd. Cotas</TableHead>
                  <TableHead>Preço de venda</TableHead>
                  <TableHead className="text-center">Distribuição</TableHead>
                  <TableHead className="text-center">Detalhes</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody striped>
                {simplifiedData?.map((productData) => {
                  const product = {
                    id: productData.contest_product_classification.id,
                    product_name: productData.contest_product_classification.product_name,
                    product_classification: {
                      name: "",
                      quota_quantity: productData.contest_product_classification.quota_total_quantity,
                      sale_price: productData.contest_product_classification.sale_price,
                    },
                    lottery_id: contestData.lottery?.id,
                  };
                  const localQuotas = quotaStates[product.id] || [];
                  const availableBalance = getAvailableBalance(product.id);

                  return (
                    <TableRow key={product.id} className="transition-colors">
                      <TableCell className="align-top">
                        {productData.contest_product_classification.product_name}
                      </TableCell>
                      <TableCell className="text-center align-top">
                        {(() => {
                          const totalQuotas =
                            productData.contest_product_classification
                              .quota_total_quantity;
                          const totalDistributed =
                            productData.contest_product_classification
                              .quota_total_distributed;

                          return (
                            <div className="flex flex-col items-center gap-1">
                              <span>{`${totalDistributed} / ${totalQuotas}`}</span>

                              {availableBalance === 0 ? (
                                <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                                  Sem saldo disponível nesta unidade
                                </span>
                              ) : (
                                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                  {availableBalance} {availableBalance > 1 ? "disponíveis" : "disponível"} em {currentUnityName}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-center align-top">
                        {Mask.formatCurrency(
                          productData.contest_product_classification.sale_price || 0
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="border rounded p-2 mb-2 max-h-40 overflow-auto">
                          {productData.unities
                            .filter((unity) => unity.id !== unityId)
                            .map((unity) => {
                              const quotaValue =
                                localQuotas.find(
                                  (q) => q.unityId === unity.id
                                )?.quota ?? 0;

                              const distributedQuota = getDistributedQuota(
                                product.id,
                                unity.id
                              );

                              return (
                                <div
                                  key={unity.id}
                                  className="flex items-center gap-2 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2"
                                >
                                  <div className="w-40 text-sm">
                                    <span className="font-medium">
                                      {unity.name}
                                    </span>
                                    {distributedQuota > 0 && (
                                      <span className="text-xs text-blue-600 dark:text-blue-400 ml-2">
                                        ({distributedQuota} distribuída
                                        {distributedQuota > 1 ? "s" : ""})
                                      </span>
                                    )}
                                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                      Cotas disponíveis:{" "}
                                      {unity.contest_product_balance.current_balance}
                                    </div>
                                  </div>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={quotaValue}
                                    onChange={(e) =>
                                      handleQuotaChange(
                                        product.id,
                                        unity.id,
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-20"
                                    disabled={distributingProductId === product.id}
                                  />
                                </div>
                              );
                            })}
                        </div>

                          {/* Validação e resumo */}
                          {(() => {
                            const validationInfo = getQuotaValidationInfo(
                              product.id,
                              localQuotas
                            );
                            if (validationInfo.totalInputQuotas === 0)
                              return null;

                            return (
                              <div className="mt-2 mb-2 p-2 rounded bg-gray-50 dark:bg-gray-800">
                                <div className="text-sm">
                                  <span className="font-medium">
                                    Total a distribuir:{" "}
                                    {validationInfo.totalInputQuotas} /{" "}
                                    {validationInfo.availableBalance}
                                  </span>
                                  {validationInfo.isOverLimit ? (
                                    <div className="text-red-600 dark:text-red-400 text-xs mt-1">
                                      ⚠️ Excesso de{" "}
                                      {validationInfo.totalInputQuotas -
                                        validationInfo.availableBalance}{" "}
                                      cotas sobre o saldo disponível
                                      {validationInfo.withHeldBalance > 0 && (
                                        <span className="block">
                                          ({validationInfo.withHeldBalance} em
                                          processamento)
                                        </span>
                                      )}
                                    </div>
                                  ) : validationInfo.remaining > 0 ? (
                                    <div className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                                      💡 Ainda restam {validationInfo.remaining}{" "}
                                      cotas disponíveis para distribuir
                                    </div>
                                  ) : (
                                    <div className="text-green-600 dark:text-green-400 text-xs mt-1">
                                      ✅ Utilizando todo o saldo disponível da
                                      unidade
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Ações de Distribuição */}
                          <div className="flex flex-col gap-2 mb-4">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Ações de Distribuição
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyDistribution(product.id)}
                              className="flex items-center gap-2 justify-start"
                            >
                              <Copy className="w-4 h-4" />
                              Copiar Distribuição Feita
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleCopyCurrentDistribution(product.id)
                              }
                              className="flex items-center gap-2 justify-start"
                              disabled={distributingProductId === product.id}
                            >
                              <Copy className="w-4 h-4" />
                              Copiar Dados Atuais
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handlePasteDistribution(product.id)
                              }
                              disabled={!copiedDistribution}
                              className="flex items-center gap-2 justify-start"
                            >
                              <Clipboard className="w-4 h-4" />
                              Colar Distribuição
                            </Button>
                          </div>

                          <Button
                            variant="outline"
                            className={cn(
                              "w-full",
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                return validationInfo.isOverLimit
                                  ? "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:border-red-700 dark:text-red-300"
                                  : colors.bg_primary;
                              })()
                            )}
                            disabled={
                              distributingProductId === product.id ||
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                const hasValidChanges = localQuotas.some(
                                  (quota) => quota.quota > 0
                                );
                                return (
                                  validationInfo.isOverLimit || !hasValidChanges
                                );
                              })()
                            }
                            onClick={() => setConfirmDialogProduct(product)}
                          >
                            {distributingProductId === product.id ? (
                              <LoaderSpin />
                            ) : (
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                const hasValidChanges = localQuotas.some(
                                  (quota) => quota.quota > 0
                                );

                                if (validationInfo.isOverLimit) {
                                  return "Distribuição bloqueada (excede saldo)";
                                }
                                if (!hasValidChanges) {
                                  return "Defina cotas para distribuir";
                                }
                                return "Fazer distribuição";
                              })()
                            )}
                          </Button>

                          <Dialog
                            open={
                              confirmDialogProduct &&
                              confirmDialogProduct.id === product.id
                            }
                            onOpenChange={(open) =>
                              !open && setConfirmDialogProduct(null)
                            }
                          >
                            <DialogContent
                              className="z-[60]"
                              style={{ zIndex: 60 }}
                            >
                              <DialogHeader>
                                <DialogTitle>
                                  Confirmar Distribuição entre Unidades
                                </DialogTitle>
                                <DialogDescription>
                                  {confirmDialogProduct &&
                                    (() => {
                                      const confirmLocalQuotas =
                                        quotaStates[confirmDialogProduct.id] ||
                                        [];
                                      const confirmTotalQuotas =
                                        confirmLocalQuotas.reduce(
                                          (sum, item) => sum + item.quota,
                                          0
                                        );
                                      return (
                                        <div className="space-y-2">
                                          <p>
                                            Você está prestes a distribuir{" "}
                                            <strong>
                                              {confirmTotalQuotas} cotas
                                            </strong>{" "}
                                            do produto:
                                          </p>
                                          <p className="font-semibold">
                                            {confirmDialogProduct.product_name}{" "}
                                            -{" "}
                                            {
                                              confirmDialogProduct
                                                .product_classification.name
                                            }
                                          </p>
                                          <p className="text-sm text-blue-600">
                                            <strong>De:</strong>{" "}
                                            {currentUnityName}
                                          </p>
                                          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                                            <p className="text-sm mb-2">
                                              <strong>
                                                Distribuição por unidade:
                                              </strong>
                                            </p>
                                            <div className="space-y-1 mb-3">
                                              {confirmLocalQuotas.map(
                                                (quota) => {
                                                  const productData =
                                                    simplifiedData.find(
                                                      (p) =>
                                                        p
                                                          .contest_product_classification
                                                          .id === product.id
                                                    );
                                                  const unity =
                                                    productData?.unities.find(
                                                      (u) =>
                                                        u.id === quota.unityId
                                                    );
                                                  const quotaValue =
                                                    quota.quota;
                                                  if (quotaValue > 0) {
                                                    return (
                                                      <div
                                                        key={quota.unityId}
                                                        className="text-sm"
                                                      >
                                                        <span className="font-medium">
                                                          {unity?.name}
                                                        </span>{" "}
                                                        -{" "}
                                                        <span className="text-blue-600 dark:text-blue-400">
                                                          {quotaValue} cotas
                                                        </span>
                                                      </div>
                                                    );
                                                  }
                                                  return null;
                                                }
                                              )}
                                            </div>
                                            <div className="border-t pt-2">
                                              <p className="text-sm">
                                                <strong>
                                                  Total de cotas a distribuir:
                                                </strong>{" "}
                                                {confirmTotalQuotas}
                                              </p>
                                              <p className="text-sm">
                                                <strong>
                                                  Saldo disponível na unidade:
                                                </strong>{" "}
                                                {getAvailableBalance(
                                                  confirmDialogProduct.id
                                                )}
                                              </p>
                                              {getAvailableBalance(
                                                confirmDialogProduct.id
                                              ) > confirmTotalQuotas && (
                                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                                  <strong>
                                                    Restará na unidade:
                                                  </strong>{" "}
                                                  {getAvailableBalance(
                                                    confirmDialogProduct.id
                                                  ) - confirmTotalQuotas}{" "}
                                                  cotas
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          <p className="text-sm text-gray-600 dark:text-gray-400">
                                            Esta ação não pode ser desfeita.
                                            Deseja continuar?
                                          </p>
                                        </div>
                                      );
                                    })()}
                                </DialogDescription>
                              </DialogHeader>
                              <FileUpload
                                files={selectedFiles}
                                onFilesChange={setSelectedFiles}
                                required={true}
                                inputId={`file-upload-unity-to-unity-${product.id}`}
                              />
                              <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setConfirmDialogProduct(null);
                                    setSelectedFiles([]);
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  onClick={() => {
                                    if (confirmDialogProduct) {
                                      handleSubmitDistribution(
                                        confirmDialogProduct
                                      );
                                      setConfirmDialogProduct(null);
                                    }
                                  }}
                                  className={cn(colors.bg_primary)}
                                  disabled={selectedFiles.length === 0}
                                >
                                  Confirmar Distribuição
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                        <TableCell className="text-center align-top">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              handleShowDetails(
                                product.id,
                                `${product?.product_name} - ${product?.product_classification?.name}`
                              )
                            }
                            className="h-8 w-8"
                            title="Ver detalhes da distribuição"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {simplifiedData?.length < 1 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        Nenhum produto encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Paginação */}
          {paginationInfo && paginationInfo.last_page > 1 && (
            <div className="flex justify-between items-center mt-4 pt-4 border-t flex-shrink-0">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Página {paginationInfo.current_page} de {paginationInfo.last_page}
              </span>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={cn("h-8", colors.bg_primary)}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className={cn(
                        "size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                      )}
                    >
                      <span className="pointer-events-none dark:text-[#F4F4F5]">
                        {paginationInfo.current_page}
                      </span>
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="text-black h-8 hover:bg-stone-950/10"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(paginationInfo.last_page);
                      }}
                    >
                      <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={cn("h-8", colors.bg_primary)}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        <Dialog
          open={showDistributeAllDialog}
          onOpenChange={setShowDistributeAllDialog}
        >
          <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
            <DialogHeader>
              <DialogTitle>
                Confirmar Distribuição Geral entre Unidades
              </DialogTitle>
              <DialogDescription>
                <div className="space-y-2">
                  <p>
                    Você está prestes a distribuir cotas para{" "}
                    <strong>todos os produtos</strong> do concurso:
                  </p>
                  <p className="font-semibold">
                    {contestData?.name} - {contestData?.lottery?.name}
                  </p>
                  <p className="text-sm text-blue-600">
                    <strong>De:</strong> {currentUnityName}
                  </p>
                  <p className="text-sm text-slate-500">
                    (de todas as páginas)
                  </p>

                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <p className="text-sm mb-2">
                      <strong>Produtos que serão distribuídos:</strong>
                    </p>
                    <div
                      className="h-40 w-full overflow-y-auto border rounded p-2"
                      style={{ maxHeight: "160px" }}
                    >
                      <div className="space-y-1">
                        {(!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) ? (
                          <div className="text-sm text-gray-500 dark:text-gray-400 p-2 text-center">
                            Nenhuma distribuição definida. Defina cotas para os produtos primeiro.
                          </div>
                        ) : (
                          Object.entries(distributeAllSnapshot)
                            .filter(([_, quotas]) => quotas.some(q => q.quota > 0))
                            .map(([productId, quotas]) => {
                              const totalQuotas = quotas.reduce((sum, item) => sum + item.quota, 0);
                              const productName = productNamesCache[productId]?.productName || `Produto ${productId}`;

                              return (
                                <div
                                  key={productId}
                                  className="text-sm p-3 rounded border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                                >
                                  <div className="font-medium mb-2">{productName}</div>
                                  <div className="space-y-1 mb-2">
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                      Distribuição por unidade:
                                    </p>
                                    {quotas.filter(q => q.quota > 0).map((quota) => {
                                      const unityName = productNamesCache[productId]?.unities?.[quota.unityId] || `Unidade ${quota.unityId}`;
                                      return (
                                        <div key={quota.unityId} className="text-xs">
                                          <span className="font-medium">{unityName}</span> -{" "}
                                          <span className="text-blue-600 dark:text-blue-400">
                                            {quota.quota} cotas
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <div className="border-t pt-2">
                                    <div className="text-xs">
                                      <strong>Total a distribuir:</strong> {totalQuotas}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Esta ação irá processar todos os produtos com cotas
                    definidas. Deseja continuar?
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <FileUpload
              files={distributeAllFiles}
              onFilesChange={setDistributeAllFiles}
              inputId="file-upload-distribute-all-unity-unity"
            />
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDistributeAllDialog(false);
                  setDistributeAllFiles([]);
                  setDistributeAllSnapshot(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDistributeAll}
                className={cn(colors.bg_primary)}
                disabled={loading || distributeAllFiles.length === 0 || !distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0}
              >
                {loading ? (
                  <>
                    <LoaderSpin />
                    Distribuindo...
                  </>
                ) : (
                  "Confirmar Distribuição Geral"
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de detalhes da distribuição */}
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-4xl z-[60]" style={{ zIndex: 60 }}>
            <DialogHeader>
              <DialogTitle>
                Detalhes da Distribuição - {selectedProductDetails?.name}
              </DialogTitle>
              <DialogDescription>
                Histórico completo das distribuições para este produto
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              {productDetails.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Unidade Origem</TableHead>
                      <TableHead>Unidade Destino</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Criado por</TableHead>
                      <TableHead>Atualizado em</TableHead>
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productDetails.map((detail: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          {detail.from_unity?.name ||
                            `Unidade ${detail.from_unity_id}`}
                        </TableCell>
                        <TableCell>
                          {detail.to_unity?.name ||
                            `Unidade ${detail.unity_id}`}
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {detail.quota_quantity}
                          </span>{" "}
                          cotas
                        </TableCell>
                        <TableCell>
                        {detail.created_at
                          ? new Date(
                              new Date(detail.created_at).toLocaleString("en-US", {
                                timeZone: "America/Fortaleza",
                              })
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : "N/A"}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              detail.status?.id == 1
                                ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                : detail.status?.id == 2
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                            }`}
                          >
                            {detail.status?.id == 1
                              ? "Pendente"
                              : detail.status?.id == 2
                              ? "Aceito"
                              : "Rejeitado"}
                          </span>
                        </TableCell>
                        <TableCell>
                          {detail.created_by?.name || "N/A"}
                        </TableCell>

                        <TableCell>
                        {detail.processed_at
                          ? new Date(
                              new Date(detail.processed_at).toLocaleString("en-US", {
                                timeZone: "America/Fortaleza",
                              })
                            ).toLocaleString("pt-BR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })
                          : "N/A"}
                        </TableCell>
                        <TableCell className="text-center">
                          {detail.files && detail.files.length > 0 ? (
                            detail.files.length === 1 ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => window.open(detail.files[0].file_url, "_blank")}
                                title={detail.files[0].file_name || "Baixar arquivo"}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    title={`${detail.files.length} arquivos`}
                                  >
                                    <Download className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {detail.files.map((file: any) => (
                                    <DropdownMenuItem
                                      key={file.id}
                                      onClick={() => window.open(file.file_url, "_blank")}
                                      className="cursor-pointer"
                                    >
                                      <Download className="h-4 w-4 mr-2" />
                                      {file.file_name || `Arquivo ${file.id}`}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma distribuição encontrada para este produto
                </div>
              )}
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowDetailsModal(false)}
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Importação Excel */}
        <Dialog open={showImportModal} onOpenChange={(open) => {
          if (!open) {
            setShowImportModal(false);
            setImportFile(null);
            setImportPreview(null);
            setImportFiles([]);
          }
        }}>
          <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col z-[60]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                Importar Distribuição Entre Unidades via Excel
              </DialogTitle>
              <DialogDescription>
                Selecione um arquivo Excel com a distribuição preenchida para importar.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Upload de arquivo */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Arquivo Excel</label>
                <div
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
                    importFile
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-300 dark:border-gray-600 hover:border-green-400"
                  )}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleImportFile(file);
                      }
                    }}
                    className="hidden"
                    id="excel-import-unity"
                  />
                  <label
                    htmlFor="excel-import-unity"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    {importFile ? (
                      <>
                        <FileSpreadsheet className="w-10 h-10 text-green-600" />
                        <span className="text-green-700 dark:text-green-400 font-medium">
                          {importFile.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          Clique para trocar o arquivo
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">
                          Clique para selecionar um arquivo Excel
                        </span>
                        <span className="text-xs text-gray-400">
                          Formatos aceitos: .xlsx, .xls
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Loading */}
              {isImporting && (
                <div className="flex items-center justify-center py-8">
                  <LoaderSpin />
                  <span className="ml-2 text-gray-600">Processando arquivo...</span>
                </div>
              )}

              {/* Preview */}
              {importPreview && !isImporting && (
                <div className="space-y-4">
                  {/* Resumo */}
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                    <h4 className="font-semibold mb-3">Resumo da Importação</h4>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      {/* Concurso + Loteria */}
                      <div className="flex items-center gap-3">
                        <div
                          className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold"
                          style={{ backgroundColor: contestData?.lottery?.color || '#666' }}
                        >
                          {importPreview.contest?.lottery}
                        </div>
                        <span className="text-lg font-bold">{importPreview.contest?.name}</span>
                      </div>
                      {/* Totais */}
                      <div className="flex items-center gap-6 text-sm">
                        <div>
                          <span className="text-gray-500">Total de Produtos:</span>
                          <span className="font-medium ml-1">{importPreview.summary?.total_products}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total de Cotas:</span>
                          <span className="font-medium text-green-600 ml-1">{importPreview.summary?.total_quotas}</span>
                        </div>
                        {importPreview.summary?.total_errors > 0 && (
                          <div>
                            <span className="text-gray-500">Erros:</span>
                            <span className="font-medium text-red-600 ml-1">{importPreview.summary?.total_errors}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Erros */}
                  {importPreview.errors && importPreview.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <h4 className="font-semibold text-red-700 dark:text-red-400 flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4" />
                        Erros Encontrados
                      </h4>
                      <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 max-h-32 overflow-y-auto">
                        {importPreview.errors.map((error: string, idx: number) => (
                          <li key={idx}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {importPreview.warnings && importPreview.warnings.length > 0 && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4" />
                        Avisos
                      </h4>
                      <ul className="text-sm text-yellow-600 dark:text-yellow-400 space-y-1 max-h-32 overflow-y-auto">
                        {importPreview.warnings.map((warning: string, idx: number) => (
                          <li key={idx}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Lista de distribuições */}
                  {importPreview.distributions && importPreview.distributions.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 dark:bg-slate-700 px-4 py-2 font-medium text-sm">
                        Distribuições a serem importadas
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Produto</TableHead>
                              <TableHead>Unidades</TableHead>
                              <TableHead className="text-right">Total Cotas</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {importPreview.distributions.map((dist: any, idx: number) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">
                                  {dist.product_name}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-wrap gap-1">
                                    {dist.unities?.slice(0, 3).map((unity: any) => (
                                      <span
                                        key={unity.id}
                                        className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded"
                                      >
                                        {unity.name}: {unity.quota_quantity}
                                      </span>
                                    ))}
                                    {dist.unities?.length > 3 && (
                                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded">
                                        +{dist.unities.length - 3} unidade(s)
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right font-medium text-green-600">
                                  {dist.total_distributed}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Upload de comprovantes */}
                  {importPreview.distributions && importPreview.distributions.length > 0 && (
                    <div className="border-t pt-4">
                      <FileUpload
                        files={importFiles}
                        onFilesChange={setImportFiles}
                        inputId="file-upload-import-unity"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportPreview(null);
                  setImportFiles([]);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmImport}
                disabled={
                  !importPreview ||
                  importPreview.distributions?.length === 0 ||
                  importFiles.length === 0 ||
                  isConfirmingImport
                }
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isConfirmingImport ? (
                  <>
                    <LoaderSpin />
                    Importando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirmar Importação
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
    </>
  );
}
